import { API_CONFIG, apiCall, getHeadersWithOrg } from './client';
import { PLATFORM_ENDPOINTS } from '../../config/config';
import {
  AccessControlConfigPayload,
  AccessControlResponse,
  SigningKeyResponse,
} from '../../types';

/**
 * Fetch the access control schema + signing-key metadata for a site.
 * Returns `access_control_config: null` when none is configured.
 * Requires the `site:manage:access_control` permission.
 */
export async function getAccessControlConfig(siteId: string): Promise<AccessControlResponse> {
  return apiCall<AccessControlResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.ACCESS_CONTROL(siteId)}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Replace the allowed-metadata schema for a site.
 * Pass `null` to clear all access control configuration.
 * Requires the `site:manage:access_control` permission.
 */
export async function updateAccessControlConfig(
  siteId: string,
  config: AccessControlConfigPayload | null,
): Promise<AccessControlResponse> {
  return apiCall<AccessControlResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.ACCESS_CONTROL(siteId)}`,
    {
      method: 'PUT',
      headers: getHeadersWithOrg(),
      body: JSON.stringify({ access_control_config: config }),
    },
  );
}

/**
 * Create or rotate the signing key. Returns the plaintext secret once — it is
 * never returned again after the response is consumed.
 * Requires the `site:access_control:key_download` permission.
 */
export async function generateSigningKey(siteId: string): Promise<SigningKeyResponse> {
  return apiCall<SigningKeyResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.ACCESS_CONTROL_SIGNING_KEY(siteId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Re-retrieve the current signing secret (for programmatic consumers).
 * Requires the `site:access_control:key_download` permission.
 */
export async function getSigningKey(siteId: string): Promise<SigningKeyResponse> {
  return apiCall<SigningKeyResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.ACCESS_CONTROL_SIGNING_KEY(siteId)}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}
